import React, { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { MODULE_VERSION } from '../version';
import { canWith } from '../utils/rbacPermissions';
import { rbacModuleForPath } from '../utils/navRbac';

/** Orden enterprise v0.3.1 — People OS + Flow IA */
const NAV_ITEMS = [
  { to: '/', end: true, labelKey: 'nav.dashboard', icon: '◎' },
  { to: '/employee-portal', labelKey: 'nav.employeePortal', icon: '◇' },
  { to: '/employees', labelKey: 'nav.employees', icon: '◉' },
  { to: '/employee-lifecycle', labelKey: 'nav.employeeLifecycle', icon: '↻' },
  { to: '/org-chart', labelKey: 'nav.orgChart', icon: '⊞' },
  { to: '/records', labelKey: 'nav.records', icon: '▣' },
  { to: '/attendance', labelKey: 'nav.attendance', icon: '⏱' },
  { to: '/leaves', labelKey: 'nav.leaves', icon: '◷' },
  { to: '/expense-reports', labelKey: 'nav.expenses', icon: '⧗' },
  { to: '/personal-finance', labelKey: 'nav.personalFinance', icon: '◐' },
  { to: '/communications', labelKey: 'nav.communications', icon: '✉' },
  { to: '/feed', labelKey: 'nav.feed', icon: '▦' },
  { to: '/chat', labelKey: 'nav.chat', icon: '💬' },
  { to: '/notifications', labelKey: 'nav.notifications', icon: '◆' },
  { to: '/performance', labelKey: 'nav.performance', icon: '◇' },
  { to: '/training', labelKey: 'nav.training', icon: '▤' },
  { to: '/surveys', labelKey: 'nav.surveys', icon: '✦' },
  { to: '/awards', labelKey: 'nav.awards', icon: '★' },
  { to: '/people-intelligence', labelKey: 'nav.peopleIntelligence', icon: '◈' },
  { to: '/reports', labelKey: 'nav.reports', icon: '▥' },
  { to: '/settings', labelKey: 'nav.settings', icon: '⚙' },
];

function NavItem({ to, end, labelKey, icon, collapsed, onNavigate, isPi }) {
  const { t } = useApp();
  const label = t(labelKey);
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `sidebar-link${isActive ? ' is-active' : ''}${isPi ? ' sidebar-link--pi' : ''}${collapsed ? ' sidebar-link--collapsed' : ''}`
      }
      onClick={onNavigate}
      title={collapsed ? label : undefined}
    >
      <span className="sidebar-link__icon" aria-hidden>
        {icon}
      </span>
      <span className="sidebar-link__row">
        <span className="sidebar-link__text">{label}</span>
        {isPi ? (
          <span className="sidebar-pi-badge" title="People Intelligence">
            {collapsed ? t('sidebar.navPiBadgeShort') : t('sidebar.navPiBadge')}
          </span>
        ) : null}
      </span>
    </NavLink>
  );
}

export function Sidebar({ mobileOpen, collapsed, onToggleCollapse, onNavigate }) {
  const { t, permSummary, isProductionUi } = useApp();
  const versionLabel = MODULE_VERSION;

  const visibleNavItems = useMemo(() => {
    return NAV_ITEMS.filter((item) => {
      const mod = rbacModuleForPath(item.to);
      if (!mod) return true;
      return canWith(permSummary, mod, 'view', isProductionUi);
    });
  }, [permSummary, isProductionUi]);

  return (
    <aside className={`app-sidebar ${mobileOpen ? 'is-open' : ''} ${collapsed ? 'app-sidebar--collapsed' : ''}`}>
      <div className="app-sidebar__top">
        <div className="app-sidebar__brand">
          <div className="app-sidebar__logo" aria-hidden>
            S
          </div>
          <div className="app-sidebar__brand-text">
            <div className="app-sidebar__name">{t('brand')}</div>
            <div className="app-sidebar__tagline">{t('brandTagline')}</div>
          </div>
        </div>
        <button
          type="button"
          className="sidebar-collapse-toggle"
          onClick={onToggleCollapse}
          aria-expanded={!collapsed}
          aria-label={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
          title={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
        >
          {collapsed ? '⟩' : '⟨'}
        </button>
      </div>

      <div className="app-sidebar__nav-scroll">
        <nav className="app-sidebar__nav" aria-label="Main">
          <div className="sidebar-group sidebar-group--flat">
            <div className="sidebar-group__label">{t('navGroups.workspace')}</div>
            <div className="sidebar-group__links">
              {visibleNavItems.map((item) => (
                <NavItem
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  labelKey={item.labelKey}
                  icon={item.icon}
                  collapsed={collapsed}
                  onNavigate={onNavigate}
                  isPi={item.to === '/people-intelligence'}
                />
              ))}
            </div>
          </div>
        </nav>
      </div>

      <div className="app-sidebar__footer">
        <span className="version-pill" title={versionLabel}>
          {collapsed ? versionLabel.slice(0, 12) + '…' : versionLabel}
        </span>
      </div>
    </aside>
  );
}
